// packages/console/src/components/panels/SourcePanel.tsx
//
// Source file inspector. Follows the job-queue selection. The probe mediainfo
// (container, codec, resolution, chroma, bitrate, duration, stream inventory)
// is source truth from FT-3, fetched from GET /v1/jobs/{id}/source. Every
// probe-derived row renders the em dash until that fetch resolves (or stays em
// dash if the source has not been probed yet), rather than fabricating the
// prototype's SRC constants (R10(b)). The poster shows the FT-3 auto-generated
// scene-aware frame for the job by default, and stays a user-fillable slot (see
// PosterSlot) where a dropped still overrides the server frame.

import { useEffect, useState } from "react";
import { Panel, Row, Eb } from "../Parts";
import { Icon } from "../Icons";
import { PosterSlot } from "../PosterSlot";
import { EM_DASH, fmtDur } from "../../lib/format";
import { fetchSourceMediaInfo, type Job, type SourceMediaInfo } from "../../api/jobs";

// cleanContainer condenses ffprobe's format_name (often a comma list like
// "mov,mp4,m4a,3gp,3g2,mj2") to a single readable token for the UI: prefer a
// well-known member if present, else the first token, uppercased.
function cleanContainer(raw?: string): string {
  if (!raw) return EM_DASH;
  const parts = raw.split(",").map((s) => s.trim()).filter(Boolean);
  if (parts.length === 0) return EM_DASH;
  const prefer = ["mp4", "webm", "mov", "mkv", "matroska"];
  const pick = parts.find((p) => prefer.includes(p)) ?? parts[0];
  return pick.toUpperCase();
}

// mbps renders a bit rate as "X.X Mbps", or the em dash when absent/zero.
function mbps(bps?: number): string {
  if (!bps || bps <= 0) return EM_DASH;
  return `${(bps / 1_000_000).toFixed(1)} Mbps`;
}

// streamLabel summarizes a stream track: its codec and count, or "none".
function streamLabel(count: number, codec?: string): string {
  if (!count) return "none";
  const c = codec ? codec.toUpperCase() : "present";
  return count > 1 ? `${c} · ${count}` : c;
}

export function SourcePanel({ job }: { job: Job | null }) {
  // The source object key is content-addressed; the display name is not on the
  // job record, so we show the object key's tail as the closest honest label.
  const fileLabel = job ? job.sourceObjectKey.split("/").pop() ?? job.sourceObjectKey : EM_DASH;

  // Probe media info, fetched per selected job. Null until it resolves, or when
  // the source has not been probed yet (the fields then stay em dashes).
  const jobId = job?.id ?? null;
  const [mi, setMi] = useState<SourceMediaInfo | null>(null);
  useEffect(() => {
    setMi(null);
    if (!jobId) return;
    const ctrl = new AbortController();
    fetchSourceMediaInfo(jobId, ctrl.signal)
      .then((info) => setMi(info))
      .catch(() => setMi(null));
    return () => ctrl.abort();
  }, [jobId]);

  const container = cleanContainer(mi?.container);
  const codecIn = mi?.videoCodec ? mi.videoCodec.toUpperCase() : EM_DASH;
  const resolution = mi?.width && mi?.height ? `${mi.width}×${mi.height}` : EM_DASH;
  const chroma = mi?.chromaSubsampling || EM_DASH;
  const sourceRate = mbps(mi?.sourceBitrateBps);
  const duration = mi && mi.durationSeconds > 0 ? fmtDur(mi.durationSeconds) : EM_DASH;

  return (
    <Panel
      title="Source file"
      style={{ flex: 1, minHeight: 0 }}
      bodyStyle={{ overflowY: "auto", flex: 1 }}
      right={<span style={{ font: "400 10px var(--font-mono)", color: "var(--fg3)" }}>{container}</span>}
    >
      <div
        style={{
          position: "relative",
          aspectRatio: "16 / 9",
          borderRadius: "var(--r-xs)",
          overflow: "hidden",
          border: "1px solid var(--line)",
          background: "var(--bg-void)",
          marginBottom: 12,
        }}
      >
        <PosterSlot jobId={job?.id ?? null} placeholder="Drop a frame from this title" />
        <span
          style={{
            position: "absolute",
            left: 8,
            top: 8,
            pointerEvents: "none",
            font: "600 9px var(--font-sans)",
            letterSpacing: ".14em",
            textTransform: "uppercase",
            color: "#fff",
            background: "rgba(3,5,6,.7)",
            padding: "4px 7px",
            borderRadius: "var(--r-xs)",
          }}
        >
          Poster
        </span>
        <span
          style={{
            position: "absolute",
            right: 8,
            bottom: 7,
            pointerEvents: "none",
            font: "400 10px var(--font-mono)",
            color: "#fff",
            background: "rgba(3,5,6,.7)",
            padding: "3px 6px",
            borderRadius: "var(--r-xs)",
          }}
        >
          {resolution}
        </span>
      </div>
      <div style={{ font: "400 12px var(--font-mono)", color: "#fff", marginBottom: 12, wordBreak: "break-all" }}>{fileLabel}</div>
      <Row label="Container" value={container} />
      <Row label="Codec in" value={codecIn} />
      <Row label="Resolution" value={resolution} />
      <Row label="Chroma" value={chroma} />
      <Row label="Source rate" value={sourceRate} />
      <Row label="Duration" value={duration} />
      <div style={{ height: 1, background: "var(--line)", margin: "12px 0" }} />
      <Eb style={{ marginBottom: 9 }}>Streams</Eb>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {[
          ["video", "V · source", mi ? streamLabel(mi.videoStreams, mi.videoCodec) : EM_DASH],
          ["audio-lines", "A · source", mi ? streamLabel(mi.audioStreams, mi.audioCodec) : EM_DASH],
          ["captions", "S · timed text", mi ? streamLabel(mi.subtitleStreams) : EM_DASH],
        ].map(([ic, label, value]) => (
          <div key={label} style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Icon name={ic} size={13} color="var(--fg3)" />
            <span style={{ font: "var(--t-body-sm)", color: "var(--fg2)", flex: 1 }}>{label}</span>
            <span style={{ font: "400 11px var(--font-mono)", color: "var(--fg3)" }}>{value}</span>
          </div>
        ))}
      </div>
    </Panel>
  );
}
